import { Event_Type } from "../../generated/core/v1/events";
import { EntityKind } from "../../generated/core/v1/financial_plan";
import { flatActions } from "./actions";
import { type Plan, eventsOf, yearlyStates } from "./core";

/**
 * The education fund: RESP balances each year, split into what the household put in, the
 * government grant (GOVERNMENT_GRANT events) and growth (the rest of the balance). When the fund
 * pays out, the three parts shrink in proportion. A split, not a calculation: every input is a plan
 * fact.
 */
export type EducationRow = {
  year: number;
  contributed: number;
  grant: number;
  growth: number;
  balance: number;
};

export function hasResp(plan: Plan): boolean {
  return !!plan?.monthlyStates.some((s) => s.accounts.some((a) => a.kind === EntityKind.RESP));
}

export function educationRows(plan: Plan): EducationRow[] {
  if (!plan) return [];
  const states = yearlyStates(plan);
  if (!states.length) return [];
  const respUuids = new Set<string>();
  plan.monthlyStates.forEach((s) =>
    s.accounts
      .filter((a) => a.kind === EntityKind.RESP)
      .forEach((a) => respUuids.add(a.entityUuid)),
  );
  if (!respUuids.size) return [];

  // Contributions and grants by year (flows), from the plan's first year.
  const contrib = new Map<number, number>();
  flatActions(plan)
    .filter((f) => respUuids.has(f.targetUuid))
    .forEach((f) => {
      const sign = f.direction === "out" ? 1 : f.direction === "in" ? -1 : 0;
      contrib.set(f.date.year, (contrib.get(f.date.year) ?? 0) + sign * f.amount);
    });
  const grants = new Map<number, number>();
  eventsOf(plan, Event_Type.GOVERNMENT_GRANT).forEach((e) => {
    if (e.details.oneofKind !== "income" || !e.date) return;
    grants.set(e.date.year, (grants.get(e.date.year) ?? 0) + e.details.income.net);
  });

  const first = states[0];
  const opening = first.accounts
    .filter((a) => respUuids.has(a.entityUuid))
    .reduce((s, a) => s + a.balance, 0);
  // The opening balance has no split in the plan: count it as contributed.
  let c = opening;
  let g = 0;
  return states.map((s, i) => {
    const y = s.date!.year;
    if (i > 0) {
      c += Math.max(0, contrib.get(y) ?? 0);
      g += grants.get(y) ?? 0;
    }
    const balance = s.accounts
      .filter((a) => respUuids.has(a.entityUuid))
      .reduce((x, a) => x + a.balance, 0);
    let contributed = c;
    let grant = g;
    if (contributed + grant > balance) {
      const k = contributed + grant > 0 ? balance / (contributed + grant) : 0;
      contributed *= k;
      grant *= k;
      c = contributed;
      g = grant;
    }
    return {
      year: y,
      contributed,
      grant,
      growth: Math.max(0, balance - contributed - grant),
      balance,
    };
  });
}

export function totalGrants(plan: Plan): number {
  return eventsOf(plan, Event_Type.GOVERNMENT_GRANT).reduce(
    (s, e) => s + (e.details.oneofKind === "income" ? e.details.income.net : 0),
    0,
  );
}
